import { useEffect, useRef } from 'react'
import * as THREE from 'three'

export default function ParticleBackground() {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!containerRef.current) return

    let renderer: THREE.WebGLRenderer | null = null
    let geometry: THREE.BufferGeometry | null = null
    let material: THREE.PointsMaterial | null = null
    let handleMouseMove: ((event: MouseEvent) => void) | null = null
    let handleResize: (() => void) | null = null
    let animationFrameId: number | null = null
    let velocities: Float32Array | null = null
    let isActive = true
    
    try {
      const scene = new THREE.Scene()
      const camera = new THREE.PerspectiveCamera(
        75,
        window.innerWidth / window.innerHeight,
        0.1,
        1000
      )
      
      // Try to create WebGL renderer with error handling
      try {
        renderer = new THREE.WebGLRenderer({ 
          alpha: true, 
          antialias: false, // Disable antialiasing for better performance
          powerPreference: 'high-performance' // Prefer performance over quality
        })
      } catch (error) {
        console.warn('WebGL not available, ParticleBackground will not render:', error)
        return // Exit early if WebGL is not available
      }
      
      if (!renderer) return
      
      renderer.setSize(window.innerWidth, window.innerHeight)
      // Cap pixel ratio for better performance on high-DPI displays
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
      containerRef.current.appendChild(renderer.domElement)

    // Subtle particle system - professional
    const particleCount = 800
    geometry = new THREE.BufferGeometry()
    const positions = new Float32Array(particleCount * 3)
    velocities = new Float32Array(particleCount * 3)
    const sizes = new Float32Array(particleCount)
    const colors = new Float32Array(particleCount * 3)

    // Science Blue color palette
    const colorPalette = [
      new THREE.Color(0x0066cc), // Science Blue
      new THREE.Color(0x0066cc), // Science Blue
      new THREE.Color(0x0066cc), // Science Blue
    ]

    for (let i = 0; i < particleCount * 3; i += 3) {
      positions[i] = (Math.random() - 0.5) * 150
      positions[i + 1] = (Math.random() - 0.5) * 150
      positions[i + 2] = (Math.random() - 0.5) * 150

      // Vary velocities for more organic movement
      const speed = 0.01 + Math.random() * 0.03
      velocities[i] = (Math.random() - 0.5) * speed
      velocities[i + 1] = (Math.random() - 0.5) * speed
      velocities[i + 2] = (Math.random() - 0.5) * speed

      // Varied sizes for depth
      sizes[i / 3] = Math.random() * 0.15 + 0.05

      // Random colors from palette
      const color = colorPalette[Math.floor(Math.random() * colorPalette.length)]
      colors[i] = color.r
      colors[i + 1] = color.g
      colors[i + 2] = color.b
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))
    geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1))

    // Subtle material with very low opacity for light theme
    material = new THREE.PointsMaterial({
      size: 0.1,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.15,
      vertexColors: true,
      blending: THREE.AdditiveBlending,
    })

    const particles = new THREE.Points(geometry, material)
    scene.add(particles)

    camera.position.z = 60

    // Smooth mouse interaction - Creative Mints style
    let mouseX = 0
    let mouseY = 0
    let targetMouseX = 0
    let targetMouseY = 0

    handleMouseMove = (event: MouseEvent) => {
      targetMouseX = (event.clientX / window.innerWidth) * 2 - 1
      targetMouseY = -(event.clientY / window.innerHeight) * 2 + 1
    }

    window.addEventListener('mousemove', handleMouseMove)

    let time = 0
    
    const animate = () => {
      if (!isActive || !geometry || !renderer || !velocities) return
      
      animationFrameId = requestAnimationFrame(animate)
      time += 0.01

      // Smooth mouse interpolation
      mouseX += (targetMouseX - mouseX) * 0.05
      mouseY += (targetMouseY - mouseY) * 0.05

      const positionsArray = geometry.attributes.position.array as Float32Array
      const sizesArray = geometry.attributes.size.array as Float32Array
      
      // Pre-calculate wave values to reduce Math.sin calls
      const waveBase = Math.sin(time * 2) * 0.5
      const sizeBase = 0.05 + Math.sin(time * 3) * 0.05
      
      for (let i = 0; i < particleCount * 3; i += 3) {
        const index = i / 3
        
        // Enhanced movement with wave-like motion (optimized)
        const wave = waveBase + positionsArray[i] * 0.005
        positionsArray[i] += velocities[i] + mouseX * 0.15 + wave * 0.01
        positionsArray[i + 1] += velocities[i + 1] + mouseY * 0.15 + wave * 0.01
        positionsArray[i + 2] += velocities[i + 2] + wave * 0.01

        // Pulsing size effect (optimized)
        sizesArray[index] = (sizeBase + Math.sin(index) * 0.05) * 1.5

        // Wrap around edges smoothly
        if (Math.abs(positionsArray[i]) > 75) positionsArray[i] = -positionsArray[i] * 0.9
        if (Math.abs(positionsArray[i + 1]) > 75) positionsArray[i + 1] = -positionsArray[i + 1] * 0.9
        if (Math.abs(positionsArray[i + 2]) > 75) positionsArray[i + 2] = -positionsArray[i + 2] * 0.9
      }

      geometry.attributes.position.needsUpdate = true
      geometry.attributes.size.needsUpdate = true

      // Smooth camera movement
      camera.position.x += (mouseX * 12 - camera.position.x) * 0.04
      camera.position.y += (mouseY * 12 - camera.position.y) * 0.04
      camera.rotation.z += (mouseX * 0.1 - camera.rotation.z) * 0.02
      camera.lookAt(scene.position)

      renderer.render(scene, camera)
    }

    animate()

    handleResize = () => {
      if (!renderer) return
      camera.aspect = window.innerWidth / window.innerHeight
      camera.updateProjectionMatrix()
      renderer.setSize(window.innerWidth, window.innerHeight)
    }

    window.addEventListener('resize', handleResize)
    } catch (error) {
      console.error('Error initializing ParticleBackground:', error)
      return () => {} // Return empty cleanup function if initialization fails
    }

    return () => {
      // Stop animation loop
      isActive = false
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId)
        animationFrameId = null
      }
      
      if (handleMouseMove) {
        window.removeEventListener('mousemove', handleMouseMove)
      }
      if (handleResize) {
        window.removeEventListener('resize', handleResize)
      }
      if (renderer) {
        renderer.dispose()
      }
      if (geometry) {
        geometry.dispose()
      }
      if (material) {
        material.dispose()
      }
      if (containerRef.current && renderer && containerRef.current.contains(renderer.domElement)) {
        try {
          containerRef.current.removeChild(renderer.domElement)
        } catch (error) {
          // Element may have already been removed
        }
      }
      
      // Clean up velocities array
      velocities = null
    }
  }, [])

  return <div ref={containerRef} className="absolute inset-0" />
}

